import { FC, useState, useCallback, useRef, memo } from 'react';
// Import UI Components
import { Button, Icon } from '@geneui/components';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';
// Import Styles
import styles from './buttonMenu.module.scss';

interface MenuOption {
    label: string;
    handler: () => void;
}

interface Props {
    title: string;
    options: MenuOption[];
    disabled?: boolean;
}

const ButtonMenu: FC<Props> = ({ title, options, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const handleButtonClick = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const handleOptionClick = (option: MenuOption) => {
        setIsOpen(false);
        option.handler();
    };

    useOnClickOutside(
        containerRef,
        useCallback(() => {
            setIsOpen(false);
        }, []),
    );

    return (
        <div className={styles.buttonMenu} ref={containerRef}>
            <Button
                className={styles.button}
                color="primary"
                appearance="minimal"
                flexibility="content-size"
                disabled={disabled}
                onClick={handleButtonClick}
            >
                {title} <Icon type={`bc-icon-arrow-${isOpen ? 'up' : 'down'}`} />
            </Button>
            {isOpen && (
                <div className={styles.optionsContainer}>
                    {options.map((option, index) => (
                        <div key={index} onClick={() => handleOptionClick(option)} className={styles.option}>
                            <p>{option.label}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(ButtonMenu);
